import React from 'react';
import { Upload, Eye, CheckCircle2, Truck } from 'lucide-react';

/**
 * "What Happens Next" — confidence-building timeline displayed on the
 * Review Order page (and other pre-submit surfaces) per the operator's
 * UX brief in iter114s.45. Customers reading the timeline answer the
 * question "what am I committing to?" without us getting on a support
 * call. The 4 steps are:
 *
 *   1. Upload received          — artwork already on file (✅ if hasArtwork)
 *   2. Proof generated          — system auto-generates the dieline +
 *                                 (optional) lifestyle preview
 *   3. You approve artwork      — one-click approve; nothing prints
 *                                 without sign-off
 *   4. We print & ship          — production starts the moment approval
 *                                 lands
 *
 * Visual: vertical timeline with green-circled icons. The first step
 * gets a green check when artwork is on file (most customers); the
 * remaining steps stay neutral so they read as upcoming milestones.
 *
 * No external state — pure presentational.
 */
const STEPS = [
  {
    icon: Upload,
    title: 'Upload received',
    body: 'Your artwork is safely on file.',
  },
  {
    icon: Eye,
    title: 'Proof generated',
    body: 'We auto-render a print-accurate proof + die-cut preview.',
  },
  {
    icon: CheckCircle2,
    title: 'You approve artwork',
    body: 'One click sign-off. Nothing prints without your green light.',
  },
  {
    icon: Truck,
    title: 'We print & ship',
    body: 'Production starts immediately after approval. Tracking emailed when it leaves the door.',
  },
];

export const WhatHappensNext = ({ hasArtwork = false }) => {
  return (
    <div
      className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm p-6"
      data-testid="what-happens-next"
    >
      <div className="mb-5">
        <h3 className="text-lg font-bold text-[#14281D] tracking-tight">What Happens Next</h3>
        <p className="text-xs text-[#4A7C59] mt-0.5">Your order's journey from this page to your door.</p>
      </div>
      <ol className="space-y-5">
        {STEPS.map((step, idx) => {
          const Icon = step.icon;
          const isComplete = idx === 0 && hasArtwork;
          return (
            <li key={step.title} className="flex items-start gap-4" data-testid={`whn-step-${idx + 1}`}>
              <div className="flex flex-col items-center flex-shrink-0">
                <span
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${
                    isComplete
                      ? 'bg-[#6FBF73] text-white'
                      : 'bg-[#E9F2EA] text-[#2F5D3A]'
                  }`}
                  aria-hidden="true"
                >
                  {isComplete ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </span>
                {idx < STEPS.length - 1 && (
                  <span
                    className={`flex-1 w-px ${isComplete ? 'bg-[#6FBF73]/40' : 'bg-[#E9F2EA]'} mt-1`}
                    style={{ minHeight: '20px' }}
                    aria-hidden="true"
                  />
                )}
              </div>
              <div className="pt-1.5 flex-1 pb-1">
                <h4 className={`text-sm font-bold ${isComplete ? 'text-[#2F5D3A]' : 'text-[#14281D]'}`}>
                  {step.title}
                  {isComplete && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#6FBF73]">Done</span>}
                </h4>
                <p className="text-xs text-[#4A7C59] leading-relaxed mt-1">{step.body}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default WhatHappensNext;
